import { cva, type VariantProps } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * A small status chip. Tone is semantic, never decorative.
 *
 * The TINT carries the meaning; the text is always `ink`. The obvious styling — brand-hue text on
 * its own tint — fails WCAG AA at this size: sage-deep on sage-tint is 4.07:1 and coral-deep on
 * coral-tint 3.47:1, short of the 4.5 a chip needs (11px is nowhere near "large text"). `ink` on
 * those same tints is 12.7–13.6:1. Nothing is lost: the hue still does the categorising, and the
 * chip has a text label anyway, so it never relied on colour alone.
 *
 * Task #57 has since added TEXT-tier hues (`sage-text` 4.62:1, `coral-text` 4.60:1 on their own
 * tints, and `gold-text` is now #865C15 / 4.62:1 — it used to be #8A5F16 / 4.40:1 and failed).
 * Those would now be legal here, but `ink` stays: at 11px it is 12.7–13.6:1 versus a 4.6 that
 * only just clears, and a chip's job is to be read at a glance. Prefer `ink` on tint for chips.
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 font-display text-[11px] font-medium leading-none',
  {
    variants: {
      tone: {
        neutral: 'bg-muted-tint text-ink',
        sage: 'bg-sage-tint text-ink',
        coral: 'bg-coral-tint text-ink',
        gold: 'bg-gold-tint text-ink',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

interface BadgeProps extends VariantProps<typeof badgeVariants> {
  children: ReactNode;
  className?: string;
}

export function Badge({ tone, className, children }: BadgeProps) {
  return <span className={cn(badgeVariants({ tone }), className)}>{children}</span>;
}
